import { getApiUrl } from '../config/environment';
import { isAdminPanelPath } from '../constants/adminPanelRoutes';
import {
  applyProductOpenGraphTags,
  buildProductOpenGraphPayload,
} from './productOpenGraph';
import {
  buildProductShareOgDescription,
  getProductSharePageUrl,
} from './productShareText';

export const SITE_SETTINGS_STORAGE_KEY = 'sheenwear_site_settings';
export const SITE_BRANDING_UPDATED_EVENT = 'sheenwearSiteBrandingUpdated';

const DEFAULT_TITLE_SUFFIX = 'Premium Fashion Store';

/** Logo URL for header / favicon (string or { url } from APIs). */
export function getNormalizedSiteLogoUrl(settings) {
  if (!settings || typeof settings !== 'object') return '';
  const v = settings.siteLogoUrl;
  if (typeof v === 'string') return v.trim();
  if (v && typeof v === 'object' && typeof v.url === 'string') return String(v.url).trim();
  return '';
}

function normalizeSiteSettings(parsed) {
  if (!parsed || typeof parsed !== 'object') return parsed;
  const out = { ...parsed };
  const img = out.promoPopupImageUrl;
  if (img && typeof img === 'object' && typeof img.url === 'string') {
    out.promoPopupImageUrl = img.url;
  }
  const logo = out.siteLogoUrl;
  if (logo && typeof logo === 'object' && typeof logo.url === 'string') {
    out.siteLogoUrl = logo.url;
  }
  return out;
}

/** Parse maintenance flag from public API (boolean, string, or snake_case field). */
export function parseMaintenanceModeFromPayload(payload) {
  if (!payload || typeof payload !== 'object') return undefined;
  const raw = payload.maintenanceMode ?? payload.maintenance_mode;
  if (raw === undefined || raw === null) return undefined;
  if (typeof raw === 'boolean') return raw;
  if (typeof raw === 'number') return raw === 1;
  if (typeof raw === 'string') {
    const s = raw.trim().toLowerCase();
    if (['true', '1', 'yes', 'on'].includes(s)) return true;
    if (['false', '0', 'no', 'off'].includes(s)) return false;
  }
  return undefined;
}

export function readSiteSettingsFromStorage() {
  try {
    const raw = localStorage.getItem(SITE_SETTINGS_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    return normalizeSiteSettings(parsed);
  } catch {
    return null;
  }
}

/** Promo image URL from site settings (string or { url } from some APIs). */
export function getPromoPopupImageUrl(settings) {
  if (!settings || typeof settings !== 'object') return '';
  const v = settings.promoPopupImageUrl;
  if (typeof v === 'string') return v.trim();
  if (v && typeof v === 'object' && typeof v.url === 'string') return String(v.url).trim();
  return '';
}

/** Clear “already shown” flags (session + local). Use ?resetPromo=1 on storefront for testing. */
export function clearPromoPopupShownFlags() {
  try {
    Object.keys(sessionStorage).forEach((k) => {
      if (k.startsWith('sheenwear_promo')) sessionStorage.removeItem(k);
    });
  } catch {
    /* ignore */
  }
  try {
    Object.keys(localStorage).forEach((k) => {
      if (k.startsWith('sheenwear_promo')) localStorage.removeItem(k);
    });
  } catch {
    /* ignore */
  }
}

export function getSiteLogoUrlFromStorage() {
  return getNormalizedSiteLogoUrl(readSiteSettingsFromStorage() || {});
}

function isAdminPath() {
  try {
    return (
      typeof window !== 'undefined' &&
      isAdminPanelPath(String(window.location.pathname || ''))
    );
  } catch {
    return false;
  }
}

/** Absolute URL for og:image / Twitter (required by Facebook, WhatsApp, etc.). */
export function toAbsoluteUrl(url) {
  if (typeof window === 'undefined' || url == null) return '';
  const s = String(url).trim();
  if (!s) return '';
  if (/^https?:\/\//i.test(s)) return s;
  if (s.startsWith('//')) return `https:${s}`;
  const origin = window.location.origin || '';
  if (!origin) return s;
  return origin + (s.startsWith('/') ? s : `/${s}`);
}

function upsertMetaProperty(property, content) {
  if (typeof document === 'undefined' || content == null || content === '') return;
  let el = document.querySelector(`meta[property="${property}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('property', property);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function upsertMetaName(name, content) {
  if (typeof document === 'undefined' || content == null || content === '') return;
  let el = document.querySelector(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('name', name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

/** Fallback og:image path: REACT_APP_OG_IMAGE (HTTPS or /path) or bundled public/logo512.png (brand, not CRA). */
function getDefaultOgImagePath(publicUrl) {
  const fromEnv = process.env.REACT_APP_OG_IMAGE;
  if (typeof fromEnv === 'string' && fromEnv.trim()) {
    const v = fromEnv.trim();
    if (/^https?:\/\//i.test(v)) return v;
    const base = publicUrl || '';
    const path = v.startsWith('/') ? v : `/${v}`;
    return `${base}${path}`.replace(/([^:]\/)\/+/g, '$1');
  }
  return (publicUrl ? `${publicUrl}/logo512.png` : '/logo512.png').replace(/([^:]\/)\/+/g, '$1');
}

/**
 * Link previews (WhatsApp, Facebook, etc.) — uses store logo when set (customer site only).
 */
function applyOpenGraphMeta(settings, siteName, logoUrl, useStoreLogoForPreview, publicUrl) {
  if (typeof document === 'undefined') return;
  const siteDesc =
    (settings.siteDescription && String(settings.siteDescription).trim()) || DEFAULT_TITLE_SUFFIX;
  const ogTitle = `${siteName} - ${DEFAULT_TITLE_SUFFIX}`;
  const default512 = getDefaultOgImagePath(publicUrl);
  const ogImage = useStoreLogoForPreview && logoUrl ? toAbsoluteUrl(logoUrl) : toAbsoluteUrl(default512);

  upsertMetaProperty('og:type', 'website');
  upsertMetaProperty('og:title', ogTitle);
  upsertMetaProperty('og:description', siteDesc);
  upsertMetaProperty('og:image', ogImage);
  upsertMetaProperty('og:site_name', siteName);

  upsertMetaName('twitter:card', 'summary_large_image');
  upsertMetaName('twitter:title', ogTitle);
  upsertMetaName('twitter:description', siteDesc);
  upsertMetaName('twitter:image', ogImage);

  const descMeta = document.querySelector('meta[name="description"]');
  if (descMeta) {
    descMeta.setAttribute('content', `${siteName} — ${siteDesc}`);
  }
}

function setFaviconLinkType(link, href) {
  if (!link || !href) return;
  const path = href.split('?')[0].toLowerCase();
  if (path.endsWith('.png')) link.setAttribute('type', 'image/png');
  else if (path.endsWith('.jpg') || path.endsWith('.jpeg'))
    link.setAttribute('type', 'image/jpeg');
  else if (path.endsWith('.webp')) link.setAttribute('type', 'image/webp');
  else if (path.endsWith('.svg')) link.setAttribute('type', 'image/svg+xml');
  else if (path.endsWith('.ico')) link.setAttribute('type', 'image/x-icon');
  else link.removeAttribute('type');
}

/** Revoked when manifest is rebuilt so blob: URLs do not leak. */
let installManifestBlobUrl = null;

function inferImageMimeFromUrl(url) {
  const p = String(url).split('?')[0].split('#')[0].toLowerCase();
  if (p.endsWith('.png')) return 'image/png';
  if (p.endsWith('.jpg') || p.endsWith('.jpeg')) return 'image/jpeg';
  if (p.endsWith('.webp')) return 'image/webp';
  if (p.endsWith('.svg')) return 'image/svg+xml';
  if (p.endsWith('.gif')) return 'image/gif';
  return 'image/png';
}

function appOriginStartUrlAndScope() {
  const pu = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const pathPart = pu.startsWith('http')
    ? (() => {
        try {
          return new URL(pu).pathname.replace(/\/$/, '') || '';
        } catch {
          return '';
        }
      })()
    : pu;
  const scopeBase = `${origin}${pathPart ? (pathPart.startsWith('/') ? pathPart : `/${pathPart}`) : ''}`;
  const scope = `${scopeBase}${scopeBase.endsWith('/') ? '' : '/'}`;
  return { start_url: scope, scope };
}

/**
 * PWA install / “Add to Home Screen” uses manifest icons. Static /manifest.json cannot follow
 * the admin-uploaded header logo; when siteLogoUrl exists, point icons at the same absolute URL as the site.
 */
function applyInstallManifest(settings, publicUrl) {
  if (typeof document === 'undefined' || typeof window === 'undefined') return;

  let link = document.querySelector('link[rel="manifest"]');
  if (!link) {
    link = document.createElement('link');
    link.setAttribute('rel', 'manifest');
    document.head.appendChild(link);
  }

  const staticHref = (
    publicUrl ? `${publicUrl}/manifest.json`.replace(/([^:])\/{2,}/g, '$1/') : '/manifest.json'
  ).replace(/([^:])\/{2,}/g, '$1');

  if (installManifestBlobUrl) {
    try {
      URL.revokeObjectURL(installManifestBlobUrl);
    } catch {
      /* ignore */
    }
    installManifestBlobUrl = null;
  }

  const logoRaw = getNormalizedSiteLogoUrl(settings || {});
  const absLogo = logoRaw ? toAbsoluteUrl(logoRaw) : '';

  if (!absLogo) {
    link.setAttribute('href', staticHref);
    return;
  }

  const siteName = settings.siteName || 'Super Wear';
  const short =
    siteName.length > 12 ? `${siteName.slice(0, 12).trim()}` : siteName;
  const siteDesc =
    (settings.siteDescription && String(settings.siteDescription).trim()) || DEFAULT_TITLE_SUFFIX;
  const mime = inferImageMimeFromUrl(absLogo);
  const { start_url, scope } = appOriginStartUrlAndScope();

  const manifestObj = {
    name: `${siteName} — ${DEFAULT_TITLE_SUFFIX}`,
    short_name: short,
    description: siteDesc,
    icons: [
      { src: absLogo, sizes: '192x192', type: mime, purpose: 'any' },
      { src: absLogo, sizes: '512x512', type: mime, purpose: 'any' },
      { src: absLogo, sizes: '512x512', type: mime, purpose: 'maskable' },
    ],
    start_url,
    scope,
    display: 'standalone',
    theme_color: '#121214',
    background_color: '#121214',
  };

  try {
    const blob = new Blob([JSON.stringify(manifestObj)], { type: 'application/json' });
    installManifestBlobUrl = URL.createObjectURL(blob);
    link.href = installManifestBlobUrl;
  } catch {
    link.setAttribute('href', staticHref);
  }
}

/**
 * Tab icon + title. When a store logo exists, use it for the tab icon on both shop and admin.
 * Default tab icon uses logo512.png (not favicon.ico) because CRA’s bundled favicon.ico is the React atom.
 * Open Graph still skips remote logos on admin routes (see useStoreLogoForOg).
 */
export function applyDocumentBranding(settings) {
  if (!settings || typeof settings !== 'object') settings = {};
  const siteName = settings.siteName || 'Super Wear';
  document.title = `${siteName} - ${DEFAULT_TITLE_SUFFIX}`;

  const publicUrl = process.env.PUBLIC_URL || '';
  const defaultFavicon = (
    publicUrl ? `${publicUrl}/logo512.png` : '/logo512.png'
  ).replace(/([^:]\/)\/+/g, '$1');
  const defaultApple = (publicUrl ? `${publicUrl}/logo192.png` : '/logo192.png').replace(
    /([^:]\/)\/+/g,
    '$1'
  );

  const logoUrl = getNormalizedSiteLogoUrl(settings);
  const onAdmin = isAdminPath();
  const useStoreLogoFavicon = Boolean(logoUrl);
  const useStoreLogoForOg = Boolean(logoUrl) && !onAdmin;

  const faviconHref = useStoreLogoFavicon ? logoUrl : defaultFavicon;

  const primaryIcon =
    document.getElementById('sheen-favicon') ||
    document.querySelector('head link[rel="icon"]');
  if (primaryIcon) {
    primaryIcon.href = faviconHref;
    setFaviconLinkType(primaryIcon, faviconHref);
  } else {
    const link = document.createElement('link');
    link.rel = 'icon';
    link.id = 'sheen-favicon';
    link.href = faviconHref;
    setFaviconLinkType(link, faviconHref);
    document.head.appendChild(link);
  }

  const shortcut = document.querySelector('link[rel="shortcut icon"]');
  if (shortcut) {
    shortcut.href = faviconHref;
    setFaviconLinkType(shortcut, faviconHref);
  }

  const appleHref = useStoreLogoFavicon ? logoUrl : defaultApple;
  let apple = document.querySelector('link[rel="apple-touch-icon"]');
  if (!apple) {
    apple = document.createElement('link');
    apple.setAttribute('rel', 'apple-touch-icon');
    document.head.appendChild(apple);
  }
  apple.href = appleHref;
  setFaviconLinkType(apple, appleHref);

  applyOpenGraphMeta(settings, siteName, logoUrl, useStoreLogoForOg, publicUrl);
  applyInstallManifest(settings, publicUrl);
}

/**
 * Product detail link previews — use pinned/primary product image (not store logo).
 * Note: WhatsApp/Facebook crawlers often skip JS; for pasted links see docs/BACKEND_PROMPT_PRODUCT_OG_SHARE.md.
 */
export function applyProductOpenGraphMeta(product) {
  if (!product || typeof document === 'undefined') return;

  const settings = readSiteSettingsFromStorage() || {};
  const siteName = settings.siteName || 'Super Wear';
  const shareUrl = getProductSharePageUrl(product);
  const pageUrl = shareUrl || (typeof window !== 'undefined' ? window.location.href : '');
  const ogDescription = buildProductShareOgDescription(product);

  const payload = buildProductOpenGraphPayload(product, {
    siteName,
    pageUrl,
    canonicalUrl: pageUrl,
    description: ogDescription,
  });
  if (payload) applyProductOpenGraphTags(payload);
}

export function notifySiteBrandingUpdated() {
  const s = readSiteSettingsFromStorage();
  applyDocumentBranding(s || {});
  window.dispatchEvent(new CustomEvent(SITE_BRANDING_UPDATED_EVENT));
}

/**
 * GET /api/settings/public — branding, promo fields, and **maintenanceMode** (when backend sends it).
 * Merges into local storage so storefront visitors see the same flags without opening admin.
 * Empty string for siteLogoUrl / siteDescription clears those fields locally (matches backend clear).
 */
export async function syncPublicSiteSettings() {
  try {
    const res = await fetch(getApiUrl('/settings/public'), {
      method: 'GET',
      headers: { Accept: 'application/json' },
      cache: 'no-store',
    });
    if (!res.ok) return false;
    const json = await res.json();
    const payload = json.data ?? json.site ?? json;
    if (!payload || typeof payload !== 'object') return false;

    let existing = readSiteSettingsFromStorage() || {};
    const patch = {};

    ['siteName', 'siteDescription'].forEach((k) => {
      const v = payload[k];
      if (v !== undefined && v !== null) patch[k] = v;
    });

    const rawLogo =
      payload.siteLogoUrl ??
      payload.site_logo_url ??
      payload.logoUrl ??
      payload.logo;
    if (rawLogo !== undefined && rawLogo !== null) {
      if (typeof rawLogo === 'object' && rawLogo.url) {
        patch.siteLogoUrl = String(rawLogo.url).trim();
      } else {
        patch.siteLogoUrl = String(rawLogo).trim();
      }
    }

    [
      'promoPopupEnabled',
      'promoPopupImageUrl',
      'promoPopupDelaySeconds',
      'promoPopupAutoCloseSeconds',
      'promoPopupShowOnHome',
      'promoPopupShowOnProductPage',
      'promoPopupShowOnShopkeeperPage',
    ].forEach((k) => {
      const v = payload[k];
      if (v !== undefined && v !== null) patch[k] = v;
    });

    ['siteEmail', 'sitePhone', 'currency', 'timezone'].forEach((k) => {
      const v = payload[k];
      if (v !== undefined && v !== null && v !== '') patch[k] = v;
    });

    const maint = parseMaintenanceModeFromPayload(payload);
    if (maint !== undefined) patch.maintenanceMode = maint;

    if (Object.keys(patch).length === 0) return false;
    existing = { ...existing, ...patch };
    localStorage.setItem(SITE_SETTINGS_STORAGE_KEY, JSON.stringify(existing));
    return true;
  } catch {
    return false;
  }
}
